import { isNullOrUndefined } from "src/services/predicates";
import { assertEnum } from "src/types/assert";
import * as GQL from "src/types/graphql";
import { z } from "zod";

export const StatusTriggerSchema = z.object({
  id: z.string().uuid(),
  type: z.string().min(1, "This field is required"),
  status: z.string().min(1, "This field is required"),
  schoolIdsOperator: z.string(),
  schoolIds: z.array(z.string()),
});

export const ActionSchema = z.object({
  type: z.string().min(1, "This field is required"),
  nextFormTemplateId: z.string().min(1, "This field is required"),
});

export const MaxCharacter = {
  title: 100,
  subtitle: 100,
  body: 300,
  buttonText: 25,
};
const NextStepSchema = z.object({
  title: z.string(),
  titleLexical: z.unknown(),
  subtitle: z.string(),
  subtitleLexical: z.unknown(),
  body: z.string(),
  bodyLexical: z.unknown(),
  buttonText: z.string(),
  buttonTextLexical: z.unknown(),
});
export type NextStep = z.infer<typeof NextStepSchema>;

export const FormikSchema = z.object({
  id: z.string().uuid(),
  trigger: StatusTriggerSchema,
  action: ActionSchema,
  nextStep: NextStepSchema.superRefine((val, ctx) => {
    // Title
    if (val.title.length < 1)
      ctx.addIssue({
        code: "custom",
        path: ["titleLexical"],
        message: "This field is required",
      });
    if (val.title.length > MaxCharacter.title)
      ctx.addIssue({
        code: "custom",
        path: ["titleLexical"],
        message: `This field has exceeded maximum allowed characters ${MaxCharacter.title}`,
      });

    // Subtitle
    if (val.subtitle.length < 1)
      ctx.addIssue({
        code: "custom",
        path: ["subtitleLexical"],
        message: "This field is required",
      });
    if (val.subtitle.length > MaxCharacter.subtitle)
      ctx.addIssue({
        code: "custom",
        path: ["subtitleLexical"],
        message: `This field has exceeded maximum allowed characters ${MaxCharacter.subtitle}`,
      });

    // Body
    if (val.body.length < 1)
      ctx.addIssue({
        code: "custom",
        path: ["bodyLexical"],
        message: "This field is required",
      });
    if (val.body.length > MaxCharacter.body)
      ctx.addIssue({
        code: "custom",
        path: ["bodyLexical"],
        message: `This field has exceeded maximum allowed characters ${MaxCharacter.body}`,
      });

    // ButtonText
    if (val.buttonText.length < 1)
      ctx.addIssue({
        code: "custom",
        path: ["buttonTextLexical"],
        message: "This field is required",
      });
    if (val.buttonText.length > MaxCharacter.buttonText)
      ctx.addIssue({
        code: "custom",
        path: ["buttonTextLexical"],
        message: `This field has exceeded maximum allowed characters ${MaxCharacter.buttonText}`,
      });
  }),
});

export const EmptyFormik: FormikType = {
  id: "",
  trigger: {
    id: "",
    type: "",
    status: "",
    schoolIdsOperator: "",
    schoolIds: [],
  },
  action: {
    type: "",
    nextFormTemplateId: "",
  },
  nextStep: {
    title: "",
    subtitle: "",
    body: "",
    buttonText: "",
  },
};

export type FormikType = z.infer<typeof FormikSchema>;

export function toGQLStatusRule(
  rule: FormikType
): GQL.status_rule_insert_input {
  if (rule === undefined || rule === null) return {};

  const rule_id = rule.trigger.id;
  const statusOrSubstatus = rule.trigger.status;
  const nextTemplateId = rule.action.nextFormTemplateId;
  const next_step = {
    title: rule.nextStep.title,
    subtitle: rule.nextStep.subtitle,
    body: rule.nextStep.body,
    buttonText: rule.nextStep.buttonText,
  };
  const schools_operator = assertEnum(
    GQL.list_operator_enum,
    rule.trigger.schoolIdsOperator
  );

  switch (statusOrSubstatus) {
    case GQL.form_status_enum.InProgress:
    case GQL.form_status_enum.Submitted:
    case GQL.form_status_enum.Verified:
    case GQL.form_status_enum.LotteryReady:
    case GQL.form_status_enum.Admissions:
    case GQL.form_status_enum.Cancelled:
    case GQL.form_status_enum.Deleted:
      return {
        rule_id,
        next_form_template_id: nextTemplateId,
        type: GQL.status_rule_type_enum.FormStatus,
        form_status: statusOrSubstatus,
        waitlist_status: null,
        offer_status: null,
        schools_operator,
        next_step,
      };
    case GQL.waitlist_status_enum.Waitlisted:
    case GQL.waitlist_status_enum.Withdrawn:
      return {
        rule_id,
        next_form_template_id: nextTemplateId,
        type: GQL.status_rule_type_enum.WaitlistStatus,
        form_status: null,
        waitlist_status: statusOrSubstatus,
        offer_status: null,
        schools_operator,
        next_step,
      };
    case GQL.offer_status_enum.Offered:
    case GQL.offer_status_enum.Accepted:
    case GQL.offer_status_enum.Declined:
      return {
        rule_id,
        next_form_template_id: nextTemplateId,
        type: GQL.status_rule_type_enum.OfferStatus,
        form_status: null,
        waitlist_status: null,
        offer_status: statusOrSubstatus,
        schools_operator,
        next_step,
      };
    default:
      throw new Error(`Unsuppored status: ${statusOrSubstatus}`);
  }
}

export function fromGQL(
  gql: GQL.GetLegacyFormTemplateRuleByFormTemplateId
): FormikType | null {
  const formTemplateRule = gql.form_template_by_pk?.form_template_rule;
  const formEngineRules = gql.form_template_by_pk?.form_engine_rules;

  if (
    formTemplateRule === null ||
    formTemplateRule === undefined ||
    formEngineRules === undefined
  ) {
    return null;
  }

  const rule = formTemplateRule.rules[0];
  const statusRule = rule?.status_rule;
  if (!rule || !statusRule) {
    throw new Error("Missing status rule from formTemplateRule");
  }

  const result = NextStepSchema.partial().safeParse(statusRule.next_step);
  let nextStep: NextStep;
  if (isNullOrUndefined(statusRule.next_step)) {
    nextStep = {
      title: "",
      subtitle: "",
      body: "",
      buttonText: "",
    };
  } else {
    nextStep = {
      title: result.data?.title ?? "",
      subtitle: result.data?.subtitle ?? "",
      body: result.data?.body ?? "",
      buttonText: result.data?.buttonText ?? "",
    };
  }

  return {
    id: formTemplateRule.id,
    trigger: {
      id: rule.id,
      type: GQL.rule_type_enum.StatusRule,
      status:
        statusRule.form_status ??
        statusRule.offer_status ??
        statusRule.waitlist_status ??
        "",
      schoolIdsOperator: statusRule.schools_operator,
      schoolIds: statusRule.schools.map((s) => s.school.id),
    },
    action: {
      type: "AssignForm",
      nextFormTemplateId: statusRule.next_form_template_id,
    },
    nextStep,
  };
}
